import { ReactNode, useEffect, useState } from 'react';
import { useRef } from 'react';
import { Box } from '@mui/material';
import { Typography } from '@sweep-io/sweep-design';
import { SweepTypographyVariants } from '@sweep-io/sweep-design/dist/components/Typography/types';

/** Based on "ExpandableTypography" from sweep-design, but without the "show more" button. **/
/** Clicking on the text expands/collapses it**/

//TODO -adding as an option to the design system?

const addMultipleLineEllipsis = (lines?: number) => ({
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
});

type ExpandableTextProps = {
  rows?: number;
  children: ReactNode;
  color?: string;
  variant: SweepTypographyVariants;
};

const ANIMATION_DURATION = 300; // in milliseconds

const ExpandableText = (props: ExpandableTextProps) => {
  const { children, rows = 2, variant, color } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [lineHeight, setLineHeight] = useState('auto');
  const [height, setHeight] = useState('auto');
  const [needShowMore, setNeedShowMore] = useState(false);

  const myRef = useRef<any>(null);

  const onClick = () => {
    setIsAnimating(true);
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (myRef?.current && rows) {
      const refStyles = window.getComputedStyle(myRef.current);
      const currentLineHeight = Number(refStyles.lineHeight?.replace('px', ''));
      const currentHeight = Number(refStyles.height?.replace('px', ''));

      setLineHeight(currentLineHeight * rows + 'px');
      setHeight(currentHeight + 'px');

      if (!isExpanded && !needShowMore) {
        setNeedShowMore(myRef?.current.offsetHeight < myRef?.current.scrollHeight);
      }
    }
  }, [myRef, isExpanded, rows, height, lineHeight, needShowMore]);

  //If length of the next changes calculate again
  useEffect(() => {
    if (myRef?.current) {
      setNeedShowMore(myRef?.current.offsetHeight < myRef?.current.scrollHeight);
    }
  }, [children]);

  //need to add the ellipsis css only when done animating the collapse
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null;
    if (isAnimating) {
      timeout = setTimeout(() => {
        setIsAnimating(false);
      }, ANIMATION_DURATION);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isAnimating, setIsAnimating]);

  return (
    <>
      <Box
        sx={{
          cursor: 'pointer',
          maxHeight: isExpanded ? height : lineHeight,
          overflow: 'hidden',
          transition: `${ANIMATION_DURATION / 1000}s ease-in-out`,
          marginBottom: '5px',
          whiteSpace: 'break-spaces',

          '& .MuiTypography-root': {
            display: '-webkit-box', //needed to prevent jumping of content
            ...(!isAnimating && !isExpanded ? addMultipleLineEllipsis(rows) : {}),
          },
        }}
        onClick={onClick}
      >
        <Typography ref={myRef} variant={variant} color={color}>
          {children}
        </Typography>
      </Box>
    </>
  );
};

export default ExpandableText;
